import React, { useState, useEffect, useRef, useMemo } from 'react'
import { MessageSquare, Edit, Trash } from 'lucide-react'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  Button,
} from '@you/ui'
import ConversationSearch from './ConversationSearch'
import '@/styles/scrollbar.css'

export interface Conversation {
  id: string
  title: string
  lastMessage: string
  timestamp: Date
  unread?: number
}

interface ConversationListProps {
  conversations: Conversation[]
  activeConversationId: string
  onSelectConversation: (id: string) => void
  onNewConversation: () => void
  onDeleteConversation?: (id: string) => void
  onRenameConversation?: (id: string, newTitle: string) => void
}

/**
 * 对话列表组件 - 2025年现代ChatGPT风格
 * 极简白色侧边栏，现代化交互设计
 */
const ConversationList: React.FC<ConversationListProps> = ({
  conversations,
  activeConversationId,
  onSelectConversation,
  onNewConversation,
  onDeleteConversation,
  onRenameConversation,
}) => {
  const [showDeleteConfirm, setShowDeleteConfirm] = useState<string | null>(
    null
  )
  const [editingId, setEditingId] = useState<string | null>(null)
  const [editTitle, setEditTitle] = useState('')
  const [filteredConversations, setFilteredConversations] = useState<
    Conversation[]
  >([])
  const [hasSearchContent, setHasSearchContent] = useState(false)
  const debounceTimerRef = useRef<NodeJS.Timeout | null>(null)

  // 防抖搜索函数
  const debouncedSearch = (value: string) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current)
    }

    debounceTimerRef.current = setTimeout(() => {
      if (!value.trim()) {
        setFilteredConversations(conversations)
        setHasSearchContent(false)
        return
      }

      setHasSearchContent(true)
      const filtered = conversations.filter(
        (conversation) =>
          conversation.title.toLowerCase().includes(value.toLowerCase()) ||
          conversation.lastMessage.toLowerCase().includes(value.toLowerCase())
      )
      setFilteredConversations(filtered)
    }, 300)
  }

  // 当conversations变化时更新筛选结果
  useEffect(() => {
    setFilteredConversations(conversations)
  }, [conversations])

  // 处理搜索
  const handleSearch = (value: string) => {
    debouncedSearch(value)
  }

  // 获取日期分组
  const getDateGroup = (date: Date) => {
    const today = new Date()
    const yesterday = new Date(today)
    yesterday.setDate(today.getDate() - 1)
    const thirtyDaysAgo = new Date(today)
    thirtyDaysAgo.setDate(today.getDate() - 30)

    // 重置时间部分，只比较日期
    const dateOnly = new Date(
      date.getFullYear(),
      date.getMonth(),
      date.getDate()
    )
    const todayOnly = new Date(
      today.getFullYear(),
      today.getMonth(),
      today.getDate()
    )
    const yesterdayOnly = new Date(
      yesterday.getFullYear(),
      yesterday.getMonth(),
      yesterday.getDate()
    )

    if (dateOnly.getTime() === todayOnly.getTime()) {
      return '今天'
    } else if (dateOnly.getTime() === yesterdayOnly.getTime()) {
      return '昨天'
    } else if (date >= thirtyDaysAgo) {
      return '30天内'
    } else {
      return date.toLocaleDateString('zh-CN', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
      })
    }
  }

  // 按日期分组对话
  const groupedConversations = useMemo(() => {
    const groups: { [key: string]: Conversation[] } = {}

    filteredConversations.forEach((conversation) => {
      const group = getDateGroup(conversation.timestamp)
      if (!groups[group]) {
        groups[group] = []
      }
      groups[group].push(conversation)
    })

    // 按时间顺序排序分组
    const sortedGroups = Object.entries(groups).sort(([a], [b]) => {
      const order = { 今天: 0, 昨天: 1, '30天内': 2 }
      const aOrder = order[a as keyof typeof order] ?? 3
      const bOrder = order[b as keyof typeof order] ?? 3

      if (aOrder !== bOrder) {
        return aOrder - bOrder
      }

      // 如果都是日期字符串，按日期倒序排列
      if (aOrder === 3 && bOrder === 3) {
        return new Date(b).getTime() - new Date(a).getTime()
      }

      return 0
    })

    return sortedGroups
  }, [filteredConversations])

  // 处理重命名
  const handleRename = (conversation: Conversation) => {
    setEditingId(conversation.id)
    setEditTitle(conversation.title)
  }

  // 保存重命名
  const handleSaveRename = () => {
    if (editingId && editTitle.trim() && onRenameConversation) {
      onRenameConversation(editingId, editTitle.trim())
    }
    setEditingId(null)
    setEditTitle('')
  }

  // 取消重命名
  const handleCancelRename = () => {
    setEditingId(null)
    setEditTitle('')
  }

  // 处理删除确认
  const handleDeleteConfirm = (conversationId: string) => {
    setShowDeleteConfirm(conversationId)
  }

  // 执行删除
  const handleDelete = () => {
    if (showDeleteConfirm && onDeleteConversation) {
      onDeleteConversation(showDeleteConfirm)
    }
    setShowDeleteConfirm(null)
  }

  return (
    <div className="w-72 min-w-72 bg-white flex flex-col rounded-xl h-full">
      {/* 顶部区域 - 使用新的搜索组件 */}
      <ConversationSearch
        onSearch={handleSearch}
        onNewConversation={onNewConversation}
      />

      {/* 对话列表 */}
      <div className="flex-1 overflow-y-auto p-4 custom-scrollbar">
        {filteredConversations.length === 0 ? (
          <div className="w-70 flex flex-col items-center justify-center h-32 text-gray-400">
            <MessageSquare size={32} className="mb-3 text-gray-300" />
            {hasSearchContent ? (
              <>
                <span className="text-sm text-gray-500">没有找到相关对话</span>
                <span className="text-xs text-gray-400 mt-1">
                  尝试其他关键词
                </span>
              </>
            ) : (
              <>
                <span className="text-sm text-gray-500">还没有对话</span>
                <span className="text-xs text-gray-400 mt-1">
                  点击上方「开启新对话」开始体验
                </span>
              </>
            )}
          </div>
        ) : (
          <div className="space-y-4">
            {groupedConversations.map(([groupName, conversations]) => (
              <div key={groupName} className="space-y-1">
                {/* 分组标题 */}
                <div className="px-2 py-1">
                  <h4 className="text-xs font-medium text-[#5C4AA4] uppercase tracking-wide">
                    {groupName}
                  </h4>
                </div>

                {/* 该分组的对话列表 */}
                {conversations.map((conversation) => (
                  <div
                    key={conversation.id}
                    className={`group relative rounded-xl cursor-pointer transition-all duration-200 ${
                      activeConversationId === conversation.id
                        ? 'bg-[rgba(130,104,235,0.06)]'
                        : 'hover:bg-gray-50'
                    }`}
                    onClick={() => {
                      console.log('ConversationList onClick:', {
                        conversationId: conversation.id,
                        activeConversationId,
                        willCallOnSelect:
                          conversation.id !== activeConversationId,
                      })
                      onSelectConversation(conversation.id)
                    }}
                  >
                    <div className="p-3">
                      <div className="flex items-center justify-between">
                        <div className="flex-1 min-w-0">
                          {editingId === conversation.id ? (
                            <input
                              type="text"
                              value={editTitle}
                              onChange={(e) => setEditTitle(e.target.value)}
                              onKeyDown={(e) => {
                                if (e.key === 'Enter') {
                                  handleSaveRename()
                                } else if (e.key === 'Escape') {
                                  handleCancelRename()
                                }
                              }}
                              onBlur={handleSaveRename}
                              className="w-full text-base font-medium text-gray-900 bg-transparent border-none outline-none focus:ring-0"
                              autoFocus
                            />
                          ) : (
                            <h3 className="text-base font-medium text-[#666666] truncate">
                              {conversation.title}
                            </h3>
                          )}
                          <p className="text-base text-[#666666] truncate">
                            {conversation.lastMessage}
                          </p>
                        </div>

                        {/* 三点菜单 */}
                        <Popover>
                          <PopoverTrigger asChild>
                            <button
                              onClick={(e) => {
                                e.stopPropagation()
                              }}
                              title="更多操作"
                              className={`p-2 rounded-lg hover:bg-gray-200 transition-all ${
                                activeConversationId === conversation.id
                                  ? 'opacity-100'
                                  : 'opacity-0 group-hover:opacity-100'
                              }`}
                            >
                              <div className="flex gap-0.5">
                                <div className="w-1 h-1 bg-[#A58BDF] rounded-full"></div>
                                <div className="w-1 h-1 bg-[#A58BDF] rounded-full"></div>
                                <div className="w-1 h-1 bg-[#A58BDF] rounded-full"></div>
                              </div>
                            </button>
                          </PopoverTrigger>
                          <PopoverContent
                            className="w-32 p-1"
                            align="start"
                            side="bottom"
                            alignOffset={-10}
                            sideOffset={10}
                          >
                            <div className="py-1">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleRename(conversation)
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded"
                              >
                                <Edit size={14} />
                                重命名
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation()
                                  handleDeleteConfirm(conversation.id)
                                }}
                                className="w-full flex items-center gap-2 px-3 py-2 text-sm text-red-600 hover:bg-red-50 rounded"
                              >
                                <Trash size={14} />
                                删除
                              </button>
                            </div>
                          </PopoverContent>
                        </Popover>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            ))}
          </div>
        )}
      </div>

      {/* 删除确认弹框 */}
      <Dialog
        open={!!showDeleteConfirm}
        onOpenChange={(open) => !open && setShowDeleteConfirm(null)}
      >
        <DialogContent className="max-w-sm">
          <DialogHeader title="永久删除对话" />
          <div className="px-6 py-4">
            <div className="flex flex-col items-center gap-4 text-center">
              <img
                src="/src/assets/images/chat/avatar_ai.png"
                alt="AI头像"
                className="w-12 h-12 rounded-full"
                onError={(e) => {
                  // 如果图片加载失败，使用默认头像
                  e.currentTarget.src =
                    'https://api.dicebear.com/7.x/avataaars/svg?seed=AI'
                }}
              />
              <div>
                <p className="text-base text-gray-600">
                  删除后，该对话将不可恢复。确认删除吗？
                </p>
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setShowDeleteConfirm(null)}
            >
              取消
            </Button>
            <Button
              className="bg-red-600 hover:bg-red-700 text-white border-red-600 hover:border-red-700"
              onClick={handleDelete}
            >
              删除
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default ConversationList
